// File: frontend/src/components/Tooltip/Tooltip.js

import React, { useState, useRef, useEffect } from 'react';
import styles from './Tooltip.module.css';

const Tooltip = ({ content, children, position = 'top', delay = 300 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(position);
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);
  
  // Handle showing tooltip with delay
  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };
  
  // Handle hiding tooltip
  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };
  
  // Adjust tooltip position if it would go off-screen
  useEffect(() => {
    if (isVisible && tooltipRef.current && containerRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let newPosition = position;
      
      // Check if tooltip would go off the right edge
      if (position === 'right' && containerRect.right + tooltipRect.width > viewportWidth) {
        newPosition = 'left';
      }
      
      // Check if tooltip would go off the left edge
      if (position === 'left' && containerRect.left - tooltipRect.width < 0) {
        newPosition = 'right';
      }
      
      // Check if tooltip would go off the top edge
      if (position === 'top' && containerRect.top - tooltipRect.height < 0) {
        newPosition = 'bottom';
      }
      
      // Check if tooltip would go off the bottom edge
      if (position === 'bottom' && containerRect.bottom + tooltipRect.height > viewportHeight) {
        newPosition = 'top';
      }
      
      setTooltipPosition(newPosition);
    }
  }, [isVisible, position]);
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return (
    <div 
      className={styles.tooltipContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      ref={containerRef}
    >
      {children}
      {isVisible && content && (
        <div 
          className={`${styles.tooltipContent} ${styles[tooltipPosition]}`}
          ref={tooltipRef}
          role="tooltip"
        >
          {content}
          <div className={styles.tooltipArrow} />
        </div>
      )}
    </div>
  );
};

export default Tooltip;